/**
 * Sorting a list by clicking its column headings.
 *
 * Every list in this product is a `<table className="data">` whose rows
 * arrive in whatever order the query happened to produce, and that order
 * is somebody's guess at what the reader wants. It is right about as
 * often as a guess: a controller reading Collections wants the biggest
 * balance first, the same screen an hour later wants the oldest, and the
 * professional reading a roster wants it alphabetical. None of those is
 * a default — they are three questions asked of one list.
 *
 * So the sort lives in the browser, over the rows already fetched. That
 * is deliberate and it is not laziness:
 *
 *   - It is instant. A sort that costs a round trip is a sort people
 *     stop using.
 *   - It cannot disagree with what is on the screen. A server sort
 *     applied to a paged list re-sorts the whole set and hands back a
 *     different page, so the row somebody was looking at vanishes.
 *
 * The consequence is honest and worth saying out loud: this sorts the
 * rows that are loaded, not the whole table. Where a list is paged, the
 * page is what gets sorted, and `paged` puts that in the heading's
 * tooltip rather than letting somebody believe otherwise. A paged list
 * that needs a true sort over everything wants an `order_by` on its
 * endpoint, which is a different piece of work.
 *
 * ---------------------------------------------------------------------
 * ADOPTING IT — three lines per table
 *
 *     const s = useSort(rows, {
 *       name:    (r) => r.display_name,
 *       balance: (r) => Number(r.balance_minor),
 *       joined:  (r) => r.joined_on,
 *     }, { key: 'name' });
 *
 *     <thead>
 *       <tr>
 *         <SortTh s={s} col="name">Member</SortTh>
 *         <SortTh s={s} col="balance" className="num">Balance</SortTh>
 *         <th>Notes</th>                       // not sortable: still a th
 *       </tr>
 *     </thead>
 *     <tbody>{s.rows.map(...)}</tbody>
 *
 * `<SortPicker s={s} labels={{ name: 'Member' }} />` above the table
 * gives the same control on a phone, where `data--cards` hides the
 * header row entirely.
 *
 * `SortTh` and `SortPicker` are module-level components taking the sort
 * as a prop, rather than components handed back by the hook. A component
 * built inside the hook is a new function on every render, which React
 * reads as a different element type and remounts — losing the focus ring
 * of the very button somebody just pressed with the keyboard.
 */

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

export type SortValue = string | number | boolean | null | undefined;
export type SortDirection = 'asc' | 'desc';

/** How each sortable column pulls its comparable value off a row. */
export type SortColumns<T> = Record<string, (row: T) => SortValue>;

interface Options {
  /** Which column to sort by before anybody clicks anything. */
  key?: string;
  dir?: SortDirection;
  /**
   * True when the rows on screen are one page of a longer list. Says so
   * in the heading's tooltip, so nobody reads a sorted page as a sorted
   * table.
   */
  paged?: boolean;
}

/**
 * Compare two cells.
 *
 * Three rules, each of which exists because the naive version gets it
 * wrong on this data:
 *
 *   - A blank sorts last in BOTH directions. A missing balance is not
 *     zero and a missing date is not the beginning of time; treating it
 *     as the smallest value puts every unknown at the top of a
 *     descending sort, which is exactly where the reader is looking.
 *   - Numbers compare as numbers. Money arrives as a bigint inside a
 *     JSON string, so '1200' < '900' lexically — the bug that would
 *     list a $9 balance above a $12,000 one on Collections.
 *   - Text compares with `numeric`, so "Table 2" precedes "Table 10"
 *     and account 1000-9 precedes 1000-10.
 */
function compare(a: SortValue, b: SortValue): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof a === 'number' && typeof b === 'number') {
    // NaN is what `Number(undefined)` gives an accessor that read a field
    // the endpoint did not send. Treated as blank rather than allowed
    // into the comparison, where it makes the ordering non-transitive and
    // scrambles the rows around it.
    if (Number.isNaN(a) && Number.isNaN(b)) return 0;
    if (Number.isNaN(a)) return 1;
    if (Number.isNaN(b)) return -1;
    return a - b;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return a === b ? 0 : a ? -1 : 1;
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export interface Sorted<T> {
  /** The rows, in order. Render these, not the ones you passed in. */
  rows: T[];
  key: string | null;
  dir: SortDirection;
  /** Column keys, in declaration order — what the phone picker offers. */
  columns: string[];
  paged: boolean;
  loaded: number;
  toggle: (col: string) => void;
  choose: (col: string | null) => void;
  flip: () => void;
}

export function useSort<T>(
  rows: T[] | null | undefined,
  columns: SortColumns<T>,
  options: Options = {},
): Sorted<T> {
  const [key, setKey] = useState<string | null>(options.key ?? null);
  const [dir, setDir] = useState<SortDirection>(options.dir ?? 'asc');

  const source = rows ?? [];
  const columnKeys = Object.keys(columns);

  const sorted = useMemo(() => {
    const get = key ? columns[key] : undefined;
    if (!get) return source;
    // Decorated with the original index so the sort is stable: two
    // members with the same balance must not swap places every time the
    // list reloads, which reads on screen as the table twitching.
    return source
      .map((row, i) => ({ row, i }))
      .sort((x, y) => {
        const c = compare(get(x.row), get(y.row));
        if (c !== 0) return dir === 'asc' ? c : -c;
        return x.i - y.i;
      })
      .map((d) => d.row);
    // `columns` is an object literal rebuilt by the caller on every
    // render. Depending on it would re-sort every render and defeat the
    // memo entirely; the keys it holds do not change between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, key, dir]);

  return {
    rows: sorted,
    key,
    dir,
    columns: columnKeys,
    paged: options.paged ?? false,
    loaded: source.length,
    toggle(col) {
      if (col === key) {
        setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setKey(col);
        // A fresh column starts ascending. Carrying the previous
        // column's direction over means clicking "Member" straight after
        // "Balance, largest first" gives you Z–A, which nobody asked for.
        setDir('asc');
      }
    },
    choose(col) {
      setKey(col);
      setDir('asc');
    },
    flip() {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    },
  };
}

/** A `<th>` that sorts. Drop-in for the `<th>` it replaces. */
export function SortTh<T>({
  s,
  col,
  children,
  className,
  style,
}: {
  s: Sorted<T>;
  col: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const active = s.key === col;
  return (
    <th
      className={className ? `${className} th--sortable` : 'th--sortable'}
      style={style}
      // What a screen reader announces, and what tells a sighted reader
      // which column the order came from. Only the active column carries
      // a direction; 'none' on the rest is what the spec asks for.
      aria-sort={active ? (s.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={active ? 'th-sort th-sort--on' : 'th-sort'}
        onClick={() => s.toggle(col)}
        title={
          s.paged
            ? `Sort by this column. Sorts the ${s.loaded} rows loaded, not the whole list.`
            : undefined
        }
      >
        <span>{children}</span>
        {/* Drawn in text rather than as an icon font or a background
            image: it has to sit on the type baseline at 11px and stay
            visible in forced-colors mode, where a background image is
            not painted at all. */}
        <span className="th-sort__caret" aria-hidden="true">
          {active ? (s.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * The same control for a phone.
 *
 * Under 860px a `data--cards` table hides its header row entirely — each
 * cell carries its own label instead — so there is nothing left to click.
 * This is hidden on a desktop, where the headers are the control.
 */
export function SortPicker<T>({
  s,
  labels,
}: {
  s: Sorted<T>;
  labels?: Record<string, string>;
}) {
  if (s.columns.length === 0) return null;
  return (
    <div className="sort-picker">
      <label>
        <span>Sort by</span>
        <select value={s.key ?? ''} onChange={(e) => s.choose(e.target.value || null)}>
          <option value="">As listed</option>
          {s.columns.map((c) => (
            <option key={c} value={c}>
              {labels?.[c] ?? c.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </label>
      <button type="button" className="secondary small" disabled={!s.key} onClick={s.flip}>
        {s.dir === 'asc' ? 'A → Z' : 'Z → A'}
      </button>
    </div>
  );
}
